import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  useContentToken,
  useCollectionBinding,
  usePropertyAddressUrl,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  condenseOfficeHours,
  formatDisplayPhone,
  stripTrailingCountry,
  type OfficeHourEntry,
} from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the address
// value becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time inside PropertyInfoPanel. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// Placeholder office hours used when the property has no office_hours
// collection data, so the Hours block always renders (mirrors the address /
// phone fallbacks above). The editor's real office-hours data overrides these.
const FALLBACK_OFFICE_HOURS: OfficeHourEntry[] = [
  { day: 'Mon', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Tue', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Wed', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Thu', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Fri', open_time: '9:00 am', close_time: '6:00 pm' },
  { day: 'Sat', open_time: null, close_time: null },
  { day: 'Sun', open_time: '9:00 am', close_time: '6:00 pm' },
];

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const formBlockPath = 'contactform.form_block';

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  const addressLabel = useContentToken('contactform.address_label');
  const phoneLabel = useContentToken('contactform.phone_label');
  const hoursLabel = useContentToken('contactform.hours_label');

  const address = useSlotGroup('contactform.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const addressHref = address.url || propertyAddressUrl || '';
  // Drop a trailing ", USA" country segment for display, mirroring the Footer.
  const addressText = stripTrailingCountry(address.text || '123 Main Street, City, ST 00000');

  const phone = useContentToken('contactform.phone') || '';
  const hasPhone = !!phone;
  const phoneDisplay = hasPhone ? formatDisplayPhone(phone) : '(555) 555-5555';

  const officeHours = useCollectionBinding<OfficeHourEntry>('contactform.office_hours', {
    collection: 'office_hours',
  });
  const hoursSource = officeHours.length > 0 ? officeHours : FALLBACK_OFFICE_HOURS;
  const hoursRows = condenseOfficeHours(hoursSource);

  return (
    <>
      <section className="cf-section">
        <div className="cf-container">
          <div className="cf-grid">
            {/* ── Left column: contact info ── */}
            <div className="cf-info-col">
              <div className="cf-info-block">
                <EditableToken path="contactform.address_label" as="h2" className="cf-info-heading">
                  {addressLabel || 'Address'}
                </EditableToken>
                <EditableSlotGroup groupPath="contactform.address" definition={addressLinkDef}>
                  {addressHref ? (
                    <a
                      href={addressHref}
                      target={address._target || '_blank'}
                      rel="noopener noreferrer"
                      className="cf-info-value cf-info-link"
                    >
                      {addressText}
                    </a>
                  ) : (
                    <p className="cf-info-value">
                      {addressText}
                    </p>
                  )}
                </EditableSlotGroup>
              </div>

              <div className="cf-info-block">
                <EditableToken path="contactform.phone_label" as="h2" className="cf-info-heading">
                  {phoneLabel || 'Phone'}
                </EditableToken>
                {hasPhone ? (
                  <a href={`tel:${phone}`} className="dniphone dniphonehref cf-info-value cf-info-link">
                    <EditableToken path="contactform.phone" as="span">
                      {phoneDisplay}
                    </EditableToken>
                  </a>
                ) : (
                  <EditableToken path="contactform.phone" as="p" className="cf-info-value">
                    {phoneDisplay}
                  </EditableToken>
                )}
              </div>

              {hoursRows.length > 0 && (
                <div className="cf-info-block">
                  <EditableToken path="contactform.hours_label" as="h2" className="cf-info-heading">
                    {hoursLabel || 'Hours'}
                  </EditableToken>
                  <div className="cf-hours-grid">
                    {hoursRows.map((row, i) => (
                      <div key={i} className="cf-hours-row">
                        <span className="cf-hours-day">{row.dayLabel}</span>
                        <span className="cf-hours-time">{row.timeLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right column: heading + lead form ── */}
            <div className="cf-form-col">
              <RichTextContent path="contactform.form_heading" as="div" className="cf-form-heading" />
              <div className="cf-form-wrap">
                <EditableToken path="contactform.form_block" as="div">
                  {formSlot ? (
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  ) : (
                    <div className="cf-form-empty-card">
                      <p className="cf-form-empty">No form block selected</p>
                    </div>
                  )}
                </EditableToken>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .cf-section {
          padding: 3rem 1.5rem;
        }
        @media (min-width: 1024px) {
          .cf-section { padding: 5rem 2rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
        }
        @media (min-width: 900px) {
          .cf-grid {
            grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
            gap: 4rem;
            align-items: start;
          }
        }

        /* ── Left column: contact info ── */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }
        .cf-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 6vw, 2rem);
          line-height: 1.1;
          font-weight: 600;
          color: var(--vibe-color-primary);
          margin: 0 0 0.75rem;
        }
        .cf-info-value {
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-info-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .cf-info-link:hover { color: var(--vibe-color-primary); }

        .cf-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .cf-hours-row {
          display: flex;
          gap: 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
        }
        .cf-hours-day { min-width: 7rem; }
        .cf-hours-time { color: var(--vibe-color-text); }

        /* ── Right column: heading + form ── */
        .cf-form-heading {
          font-family: var(--vibe-font-heading);
          font-size: 32px;
          line-height: 1.1;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0 0 1.75rem;
        }
        .cf-form-heading :first-child { margin-top: 0; }
        .cf-form-heading :last-child { margin-bottom: 0; }
        .cf-form-wrap {
          width: 100%;
        }
        /* Match the canonical PropertyOverview CTA hover on the lead-form submit
           button. The multihub-lead-form block sets its rest background via an
           inline style (var(--vibe-color-primary)), so the hover fill + white
           label must use !important to win over that inline declaration. */
        .cf-form-wrap .mh-lead-form-submit-button {
          transition: background-color 0.3s ease, color 0.3s ease;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: var(--vibe-color-surface) !important;
          color: #ffffff !important;
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_heading': {
      label: 'Form Heading',
      type: 'richtext',
      defaultValue: '<p>Contact Us</p>',
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.address_label': {
      label: 'Address Label',
      type: 'text',
      defaultValue: 'Address',
    },
    'contactform.phone_label': {
      label: 'Phone Label',
      type: 'text',
      defaultValue: 'Phone',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.hours_label': {
      label: 'Hours Label',
      type: 'text',
      defaultValue: 'Hours',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
